import React from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { Img, ScrollRow, Shimmer } from '@/components/ui';
import { PriceTag, RatingPill } from '@/components/shop/ShopUI';
import { useWishlistToggle } from '@/lib/shopStore';
import { isLightHex } from '@/lib/shop';

export const WishlistHeart = ({ productId, variantId, className = '' }) => {
  const { wishlist, toggle } = useWishlistToggle();
  const on = Boolean(wishlist?.has(productId));
  return (
    <button
      type="button"
      aria-label={on ? 'Remove from wishlist' : 'Save to wishlist'}
      aria-pressed={on}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle(productId, variantId);
      }}
      className={`lk-focus flex h-9 w-9 items-center justify-center rounded-full bg-white/95 shadow-card backdrop-blur transition hover:scale-105 active:scale-95 ${className}`}
    >
      <Heart className={`h-[18px] w-[18px] transition-colors ${on ? 'fill-danger text-danger' : 'text-ink-700'}`} />
    </button>
  );
};

export const SwatchDots = ({ swatches = [], max = 5 }) => {
  if (!swatches.length) return null;
  const shown = swatches.slice(0, max);
  return (
    <div className="flex items-center gap-1" aria-label={`${swatches.length} colours`}>
      {shown.map((hex) => (
        <span
          key={hex}
          className={`h-3.5 w-3.5 rounded-full ${isLightHex(hex) ? 'ring-1 ring-inset ring-ink-200' : ''}`}
          style={{ backgroundColor: hex }}
        />
      ))}
      {swatches.length > max && <span className="tabular text-[11px] font-medium text-ink-500">+{swatches.length - max}</span>}
    </div>
  );
};

/**
 * One product tile: image, brand, two-line title, rating, price line and the
 * colours it comes in (`badgeMin`: smallest discount that earns the corner
 * badge; deal rails pass 1). Out-of-stock products dim and say so instead of
 * disappearing, so a shared link never lands on a mystery gap.
 */
const ProductCard = ({ product, className = '', badgeMin = 40 }) => {
  const p = product;
  return (
    <Link
      to={`/shop/p/${p.slug}`}
      className={`lk-focus group flex h-full flex-col overflow-hidden rounded-2xl border border-ink-200/70 bg-white transition-shadow hover:shadow-cardHover ${className}`}
    >
      <div className="relative">
        <Img
          src={p.image}
          alt={p.title}
          ratio="4 / 5"
          className="w-full"
          imgClassName={`transition-transform duration-500 group-hover:scale-[1.04] ${p.inStock ? '' : 'grayscale-[60%] opacity-70'}`}
        />
        <WishlistHeart productId={p.id} className="absolute right-2 top-2" />
        {!p.inStock && (
          <span className="absolute inset-x-0 bottom-0 bg-ink-900/75 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-white">
            Out of stock
          </span>
        )}
        {p.inStock && p.available > 0 && p.available <= 5 && (
          <span className="absolute inset-x-0 bottom-0 bg-danger/90 py-1 text-center text-[11px] font-semibold text-white">
            Only {p.available} left
          </span>
        )}
        {p.inStock && p.discountPercent > 0 && p.discountPercent >= badgeMin && (
          <span className="absolute left-2 top-2 rounded-md bg-success px-1.5 py-0.5 text-[11px] font-bold text-white">
            {p.discountPercent}% off
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        {p.brand && <p className="truncate text-xs font-semibold uppercase tracking-wide text-ink-500">{p.brand}</p>}
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm leading-5 text-ink-900">{p.title}</h3>
        <RatingPill value={p.rating} count={p.ratingCount} />
        <div className="mt-auto pt-1">
          <PriceTag price={p.price} mrp={p.mrp} discount={p.discountPercent} />
          {p.swatches?.length > 1 && (
            <div className="mt-1.5">
              <SwatchDots swatches={p.swatches} />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export const ProductCardSkeleton = () => (
  <div className="overflow-hidden rounded-2xl border border-ink-200/70 bg-white">
    <Shimmer className="aspect-[4/5] w-full rounded-none" />
    <div className="space-y-2 p-3">
      <Shimmer className="h-3 w-16" />
      <Shimmer className="h-4 w-full" />
      <Shimmer className="h-4 w-2/3" />
      <Shimmer className="h-5 w-24" />
    </div>
  </div>
);

export const ProductGrid = ({ children }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-4">{children}</div>
);

/** Horizontally scrolling rail of cards (deals, similar products). */
export const ProductRail = ({ items = [], loading }) => (
  <ScrollRow itemClassName="w-[44vw] max-w-[220px] sm:w-[200px]">
    {loading
      ? Array.from({ length: 6 }, (_, i) => <ProductCardSkeleton key={i} />)
      : items.map((p) => <ProductCard key={p.id} product={p} />)}
  </ScrollRow>
);

export default ProductCard;
